import { SUPPORT_EMAIL } from "@common/lib/supportHours";

interface BridgeTroubleshootingProps {
    dbReachable: boolean;
    pubsubReachable: boolean;
    lastSeenAt?: string;
}

interface TroubleshootingItem {
    title: string;
    steps: string[];
}

function getTroubleshootingItems(props: BridgeTroubleshootingProps): TroubleshootingItem[] {
    const items: TroubleshootingItem[] = [];

    if (!props.dbReachable) {
        items.push({
            title: "Database unreachable",
            steps: [
                "Verify the PIMS server software is running on the same machine",
                "Check that the correct PIMS type was selected during download",
                "Restart the HappyDoc Bridge service from the system tray",
            ],
        });
    }

    if (!props.pubsubReachable) {
        items.push({
            title: "Cloud unreachable",
            steps: [
                "Check internet connectivity on the bridge machine",
                "Verify outbound HTTPS (port 443) is not blocked by firewall",
                "Restart the HappyDoc Bridge service from the system tray",
            ],
        });
    }

    if (props.lastSeenAt) {
        const minutesAgo = Math.floor((Date.now() - new Date(props.lastSeenAt).getTime()) / 60_000);
        if (minutesAgo > 15) {
            items.push({
                title: "Bridge not seen recently",
                steps: [
                    "Check if the computer was restarted — the bridge starts automatically",
                    "Look for the HappyDoc Bridge icon in the system tray (bottom-right)",
                    "Open Services (services.msc) and check that HappyDocBridge is running",
                ],
            });
        }
    }

    if (items.length === 0) {
        items.push({
            title: "Bridge is not connecting",
            steps: [
                "Verify the bridge is installed on the server, not a workstation",
                "Check the system tray for the HappyDoc Bridge icon",
                "Try restarting the HappyDoc Bridge service",
            ],
        });
    }

    return items;
}

export default function BridgeTroubleshooting(props: BridgeTroubleshootingProps) {
    const items = getTroubleshootingItems(props);

    return (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 flex flex-col gap-3">
            <p className="text-sm font-medium text-yellow-800">Troubleshooting</p>

            {items.map((item, i) => (
                <div key={i} className="text-sm">
                    <p className="font-medium text-yellow-700">{item.title}</p>
                    <ul className="list-disc list-inside text-yellow-600 mt-1 space-y-0.5">
                        {item.steps.map((step, j) => (
                            <li key={j}>{step}</li>
                        ))}
                    </ul>
                </div>
            ))}

            <div className="text-xs text-yellow-600 border-t border-yellow-200 pt-2">
                <p>
                    Log files: <code className="bg-yellow-100 px-1 rounded">C:\ProgramData\HappyDoc\Bridge\logs\</code>
                </p>
                <p className="mt-0.5">
                    Contact support at <span className="font-medium">{SUPPORT_EMAIL}</span> with your log files
                </p>
            </div>
        </div>
    );
}
